import { all, takeEvery, call, put } from 'redux-saga/effects';
import { fetchEntity } from '@classpass/saga-tools';
import { defineMessages } from 'react-intl';

import { closeModal, waitForModal } from 'components/Modal';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { reportError } from 'helpers/error-logging';
import { staffAccounts } from 'store/saga/entities/bound';

export const messages = {
  success: defineMessages({
    confirmationEmail: {
      id: 'staff-accounts.saga.success.confirmation-email',
      defaultMessage: 'Confirmation email sent to new user.',
    },
    staffUpdated: {
      id: 'staff-accounts.saga.success.staff-updated',
      defaultMessage: 'Staff updated.',
    },
    staffDeleted: {
      id: 'staff-accounts.saga.success.staff-deleted',
      defaultMessage: 'Staff deleted.',
    },
  }),
  error: defineMessages({
    problemWithRequest: {
      id: 'staff-accounts.saga.error.problem-with-request',
      defaultMessage: 'There was a problem with your request.',
    },
    duplicatedEmail: {
      id: 'staff-accounts.saga.error.duplicated-email',
      defaultMessage: 'This email has been already used.',
    },
  }),
};
export const CREATE_STAFF_ACCOUNT = 'STAFF_ACCOUNTS/CREATE_STAFF_ACCOUNT';
export const UPDATE_STAFF_ACCOUNT = 'STAFF_ACCOUNTS/UPDATE_STAFF_ACCOUNT';
export const DELETE_STAFF_ACCOUNT = 'STAFF_ACCOUNTS/DELETE_STAFF_ACCOUNT';

export const createStaffAccount = account => ({ type: CREATE_STAFF_ACCOUNT, account });
export const updateStaffAccount = account => ({ type: UPDATE_STAFF_ACCOUNT, account });
export const deleteStaffAccount = account => ({ type: DELETE_STAFF_ACCOUNT, account });

const isDuplicatedEmail = ({ response }) => response && response.status === 409;

export function* doCreateStaffAccount(client, action) {
  try {
    const { account } = action;

    yield call(fetchEntity, staffAccounts, [client, account]);
    yield call(fetchEntity, staffAccounts, [client]);
    yield put(closeModal());

    yield put(createInfoBanner(messages.success.confirmationEmail));
  } catch (error) {
    if (isDuplicatedEmail(error)) {
      yield put(createErrorBanner(messages.error.duplicatedEmail));
    } else {
      yield put(createErrorBanner(messages.error.problemWithRequest));
    }
    yield call(reportError, error);
  }
}

export function* doUpdateStaffAccount(client, action) {
  try {
    const { account } = action;

    yield call(fetchEntity, staffAccounts, [client, { ...account, active: true }]);
    yield call(fetchEntity, staffAccounts, [client]);
    yield put(closeModal());

    yield put(createInfoBanner(messages.success.staffUpdated));
  } catch (error) {
    if (isDuplicatedEmail(error)) {
      yield put(createErrorBanner(messages.error.duplicatedEmail));
    } else {
      yield put(createErrorBanner(messages.error.problemWithRequest));
    }
    yield call(reportError, error);
  }
}

export function* doDeleteStaffAccount(client, action) {
  try {
    const { account } = action;

    const confirmed = yield call(waitForModal, client, 'DeleteStaffAccountConfirmationModal');
    if (confirmed) {
      yield call(fetchEntity, staffAccounts, [client, { ...account, active: false }]);
      yield call(fetchEntity, staffAccounts, [client]);

      yield put(createInfoBanner(messages.success.staffDeleted));
    }
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* handleRouteEnter(client) {
  yield call(fetchEntity, staffAccounts, [client]);
}

export function* watchStaffAccountsActions(client) {
  yield takeEvery(CREATE_STAFF_ACCOUNT, doCreateStaffAccount, client);
  yield takeEvery(UPDATE_STAFF_ACCOUNT, doUpdateStaffAccount, client);
  yield takeEvery(DELETE_STAFF_ACCOUNT, doDeleteStaffAccount, client);
}
